import X from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentMetadataForm } from './ContentMetadataForm';
import { CreateVariantDialog } from './CreateVariantDialog';
import { HistoryEntry, HistoryGroup, HistoryVersionList } from './HistoryVersionList';
import { VersionComparisonDialog } from './VersionComparisonDialog';

import { useTextActions } from '@/features/editor/hooks/useTextActions';

import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { ContentDocument, ContentSummary, Scene } from '@/lib/types';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
const DRAWER_WIDTH = 360;

type TextDocWithHistory = ContentDocument & { history?: Record<string, HistoryEntry[]> };

interface HistoryDrawerProps {
  isOpen: boolean;
  scene: Scene | null;
  textDoc: TextDocWithHistory | null;
  versionToCompare: unknown;
  onSelectVersion: (contentId: string, dayKey: string, seq: number, id: string) => void;
  onSetAsMain: (variantId: string) => Promise<void>;
  onCloseVersionComparison: () => void;
  currentContent?: string;
  reconstructedVersionText?: string;
  onSave?: () => Promise<void>;
  onClose: () => void;
}

export function HistoryDrawer({
  isOpen,
  scene,
  textDoc,
  versionToCompare,
  onSelectVersion,
  onSetAsMain,
  onCloseVersionComparison,
  currentContent,
  reconstructedVersionText,
  onSave,
  onClose,
}: HistoryDrawerProps) {
  const { t } = useTranslation();
  const { novel, contentDao, narrativeDao } = useNovelRepository();
  const { user } = useFirebase();
  const [localName, setLocalName] = useState(textDoc?.name ?? '');
  const [localNotes, setLocalNotes] = useState(textDoc?.notes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  const { createNewText, deleteTextDoc } = useTextActions();
  const navigate = useNavigate();
  const [isCreateVariantDialogOpen, setIsCreateVariantDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const isDirty = textDoc !== null && (localName !== (textDoc.name ?? '') || localNotes !== (textDoc.notes ?? ''));

  const handleVariantChange = (variantId: string) => {
    if (variantId === textDoc?.id) return;
    // Selecting a variant is navigation only. Promotion is an explicit action.
    navigate({
      to: '/editor',
      search: { textId: variantId },
      replace: true,
    });
  };

  const handleSaveMetadata = async () => {
    if (!textDoc || !novel.id || !user || !contentDao) return;
    setIsSaving(true);
    try {
      await contentDao.updateContent(novel.ownerId, novel, textDoc.id, { name: localName, notes: localNotes });
    } catch (err: unknown) {
      console.error('Failed to save content metadata:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateVariant = async (newVariantName: string, currentVariantName: string | null) => {
    setIsCreateVariantDialogOpen(false);
    if (!scene || !novel.id || !user) return;

    if (currentVariantName && textDoc?.id && (!textDoc.name || textDoc.name.trim() === '')) {
      await contentDao?.updateContent(novel.ownerId, novel, textDoc.id, { name: currentVariantName });
    }

    const newVariantId = await createNewText(newVariantName, [scene.id]);
    if (!newVariantId) return;

    const currentVariants = scene.contentVariants || [];
    const newVariants = Array.from(new Set([...currentVariants, newVariantId]));
    await narrativeDao?.updateNarrativeItem(novel.ownerId, novel, 'scenes', scene.id, {
      contentVariants: newVariants,
    });

    navigate({
      to: '/editor',
      search: { textId: newVariantId },
      replace: true,
    });
  };

  const handleDeleteVariant = async () => {
    setIsDeleteDialogOpen(false);
    if (!textDoc || !scene || !novel.id || !narrativeDao) return;

    // Remove from scene variants
    const currentVariants = scene.contentVariants || [];
    const newVariants = currentVariants.filter(id => id !== textDoc.id);

    const isDeletingMain = textDoc.id === scene.mainContentId;
    const fallbackMainId = isDeletingMain ? (newVariants[0] || null) : scene.mainContentId;

    if (!fallbackMainId) {
      console.warn("Cannot delete the only variant");
      return;
    }

    await narrativeDao.updateNarrativeItem(novel.ownerId, novel, 'scenes', scene.id, {
      contentVariants: newVariants.filter(id => id !== fallbackMainId),
      mainContentId: fallbackMainId
    });

    // Delete the text document
    await deleteTextDoc(textDoc.id);

    // Navigate to the new main variant
    navigate({
      to: '/editor',
      search: { textId: fallbackMainId },
      replace: true,
    });
  };

  const historyGroups = useMemo((): HistoryGroup[] => {
    if (!textDoc) return [];

    const entries = Object.entries(textDoc.history ?? {})
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => ({
        date,
        items: [...items].sort((a, b) => {
          const tA = new Date(a.dateTime).getTime() || 0;
          const tB = new Date(b.dateTime).getTime() || 0;
          return tB - tA;
        }),
      }));

    const rawUpdatedAt = textDoc.audit?.updatedAt ?? new Date().toISOString();
    const updatedAt = typeof rawUpdatedAt === 'string' ? rawUpdatedAt : new Date().toISOString();
    const dateKey = updatedAt.split('T')[0];
    const currentItem: HistoryEntry = { id: 'current', seq: textDoc.scratchpadVersion ?? 0, dateTime: updatedAt, isCurrent: true };

    if (entries.length > 0 && entries[0].date === dateKey) {
      if (!entries[0].items.find(i => i.id === 'current')) entries[0].items.unshift(currentItem);
    } else {
      entries.unshift({ date: dateKey, items: [currentItem] });
    }
    return entries;
  }, [textDoc]);

  const showPromoteButton = Boolean(textDoc && scene && textDoc.id !== scene.mainContentId);

  const handlePromoteToMain = async () => {
    if (!textDoc || !showPromoteButton) return;
    setIsPromoting(true);
    try {
      await onSetAsMain(textDoc.id);
    } finally {
      setIsPromoting(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        onClose={onClose}
        anchor="right"
        open={isOpen}
        PaperProps={{ sx: { width: isMobile ? '90vw' : DRAWER_WIDTH, overflow: 'hidden' } }}
        sx={{
          width: isOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: isMobile ? '90vw' : DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: isMobile ? undefined : 'relative',
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, pl: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('editor.version_history')}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
              <X sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
            {scene && (
              <Box sx={{ p: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('editor.scene_variants')}
                    </Typography>
                    <Tooltip title={t('editor.variants_info_tooltip')} arrow placement="top">
                      <IconButton size="small" sx={{ ml: 0.5, p: 0.25, color: 'text.secondary' }}>
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {showPromoteButton && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={isPromoting}
                      onClick={() => void handlePromoteToMain()}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                    >
                      {t('editor.set_as_main')}
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={textDoc?.id ?? ''}
                      onChange={(e) => {
                        if (e.target.value === '__CREATE_NEW__') {
                          setIsCreateVariantDialogOpen(true);
                        } else {
                          void handleVariantChange(e.target.value as string);
                        }
                      }}
                      sx={{ borderRadius: 2 }}
                      renderValue={(selectedId) => {
                        if (!selectedId || selectedId === '__CREATE_NEW__') return null;
                        const summary = novel?.narrativeMeta?.summaries?.contents?.[selectedId as string] as ContentSummary | undefined;
                        const isActive = selectedId === scene?.mainContentId;
                        // Use textDoc.name if we are rendering the currently selected document to prevent visual lag
                        const nameToUse = (selectedId === textDoc?.id && textDoc?.name) ? textDoc.name : summary?.name;
                        const displayName = nameToUse || (isActive ? t('editor.current_variant_default_name') : t('editor.untitled'));
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {displayName}
                            </Typography>
                            {isActive && (
                              <Chip label="MAIN" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />
                            )}
                          </Box>
                        );
                      }}
                    >
                      {([scene.mainContentId, ...(scene.contentVariants || [])].filter((id): id is string => Boolean(id))).map((variantId) => {
                        const summary = novel?.narrativeMeta?.summaries?.contents?.[variantId] as ContentSummary | undefined;
                        if (!summary) return null;
                        const isActive = variantId === scene.mainContentId;
                        const displayName = summary.name || (isActive ? t('editor.current_variant_default_name') : t('editor.untitled'));
                        return (
                          <MenuItem key={variantId} value={variantId}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: variantId === textDoc?.id ? 700 : 500 }}>
                                  {displayName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(summary.updatedAt).toLocaleString()}
                                </Typography>
                              </Box>
                              {isActive && (
                                <Chip label="MAIN" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                      <Divider />
                      <MenuItem value="__CREATE_NEW__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        + {t('editor.create_new_variant')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title={(scene.contentVariants?.length || 0) === 0 ? t('editor.cannot_delete_only_variant') : t('editor.delete_variant')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={(scene.contentVariants?.length || 0) === 0}
                        onClick={() => setIsDeleteDialogOpen(true)}
                        sx={{
                          bgcolor: 'error.main',
                          color: 'error.contrastText',
                          '&:hover': { bgcolor: 'error.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {textDoc && (
              <ContentMetadataForm
                name={localName}
                notes={localNotes}
                isSaving={isSaving}
                isDirty={isDirty}
                onNameChange={setLocalName}
                onNotesChange={setLocalNotes}
                onSave={handleSaveMetadata}
                extraAction={
                  scene ? (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                      onClick={() => setIsCreateVariantDialogOpen(true)}
                    >
                      + {t('editor.create_new_variant')}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <HistoryVersionList
              groups={historyGroups}
              contentId={textDoc?.id ?? ''}
              onSelectVersion={onSelectVersion}
            />
          </Box>
        </Box>
      </Drawer>

      <VersionComparisonDialog
        isOpen={!!versionToCompare}
        onClose={onCloseVersionComparison}
        reconstructedVersionText={reconstructedVersionText || ''}
        currentContent={currentContent || ''}
      />
      <CreateVariantDialog
        open={isCreateVariantDialogOpen}
        onClose={() => setIsCreateVariantDialogOpen(false)}
        currentVariantNeedsName={Boolean(textDoc && (!textDoc.name || textDoc.name.trim() === ''))}
        onCreate={handleCreateVariant}
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('editor.delete_variant_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('editor.delete_variant_confirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void handleDeleteVariant()} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
